'use client';

import { useState, useEffect } from 'react';
import { BookmarkSimpleIcon } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import Latex from '../../../../../apps/frontend/src/components/global/Latex';
import BookmarkDrawerGlobal from '../../components/drawers/BookmarkDrawer';

const statusStyles = {
  wasted: { color: 'var(--q3-danger-normal)', borderColor: 'var(--q3-danger-light)' },
  confused: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-stroke-normal)' },
  overtime: { color: 'var(--q3-yellow-normal)', borderColor: 'var(--q3-yellow-light)' },
  perfect: { color: 'var(--q3-success-normal)', borderColor: 'var(--q3-success-light)' },
  default: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
  'not tagged': { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
};

const getStatusStyles = (status) => statusStyles[status?.toLowerCase()] || statusStyles.default;

export const QuestionItem = ({
  id,
  question,
  questionid,
  rawData,
  status,
  type,
  testInfo,
  note,
  chapterInfo,
  isBookmarked = true,
  packId,
  testId,
  cookies,
  onBookmarkOpen,
  isAnyDrawerOpen,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);
  const simplifyMode = useSelector((state) => state.global.simplifyMode);
  const [questionNotes, setQuestionNotes] = useState(null);
  const [questionNotebooks, setQuestionNotebooks] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const hoverEffect = 'hover:bg-[var(--q3-surface-dim)] cursor-pointer';

  const handleBookmarkClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowBookmarkDrawer(true);
    if (onBookmarkOpen) onBookmarkOpen();
  };

  const handleBookmarkDrawer = () => setShowBookmarkDrawer(!showBookmarkDrawer);

  const handleQuestionNotes = (updatedQuestionNotes) => setQuestionNotes(updatedQuestionNotes);

  const handleQuestionBoomark = (updatedQuestionNotebooks) => setQuestionNotebooks(updatedQuestionNotebooks);

  const formatType = (str) =>
    str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_\-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getHoverEffect = () => (isAnyDrawerOpen || showBookmarkDrawer ? '' : hoverEffect);

  if (isMobile) {
    return (
      <div className={` px-4 py-3 ${getHoverEffect()} `}>
        <div className="flex flex-col gap-2">
          {/* Top Row: ID and Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">{id}</span>
            {!simplifyMode && isBookmarked && (
              <button
                onClick={handleBookmarkClick}
                disabled={isAnyDrawerOpen}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]"
              >
                <BookmarkSimpleIcon size={14} weight="fill" />
                Bookmarked
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!simplifyMode && status && (
              <span className="text-xs font-medium px-2 py-0.5 rounded border" style={getStatusStyles(status)}>
                {status.toUpperCase()}
              </span>
            )}
            {!simplifyMode && (type || testInfo) && (
              <span className="text-xs text-[var(--q3-neutral-light)] flex items-center gap-1">
                {testInfo && <span>{testInfo}</span>}
                {type && testInfo && <span>•</span>}
                {type && <span>{formatType(type)}</span>}
              </span>
            )}
          </div>

          {/* Question */}
          <div className="text-sm text-[var(--q3-neutral-default)]">
            {question ? <Latex>{question}</Latex> : <span>No question text available.</span>}
          </div>

          {/* Note */}
          {!simplifyMode && note && (
            <div className="text-sm text-[var(--q3-neutral-light)]">
              <strong className="text-[var(--q3-neutral-default)]">Note:</strong> {note}
            </div>
          )}

          {/* Drawer */}
          {showBookmarkDrawer && (
            <BookmarkDrawerGlobal
              questionId={questionid}
              packId={packId}
              testId={testId}
              handleShowDrawer={handleBookmarkDrawer}
              handleQuestionNotes={handleQuestionNotes}
              handleQuestionBoomark={handleQuestionBoomark}
            />
          )}
        </div>
      </div>
    );
  }

  // Desktop view (unchanged)
  return (
    <div className={`w-full ${getHoverEffect()}`}>
      <div className="px-2 py-1">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-2">
          <div className="font-medium text-sm text-[var(--q3-neutral-default)]">{id}</div>

          <div className="flex-1 flex flex-col items-start gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {!simplifyMode && status && (
                <span className="text-xs font-medium px-2 py-0.5 rounded border" style={getStatusStyles(status)}>
                  {status.toUpperCase()}
                </span>
              )}
              {!simplifyMode && (type || testInfo) && (
                <span className="text-xs text-[var(--q3-neutral-light)] flex items-center gap-1">
                  {testInfo && <span>{testInfo}</span>}
                  {type && testInfo && <span>•</span>}
                  {type && <span>{formatType(type)}</span>}
                </span>
              )}
            </div>
            <span className="text-[var(--q3-neutral-default)] text-sm">
              {question ? <Latex>{question}</Latex> : <span>No question text available.</span>}
            </span>
          </div>
          {!simplifyMode && isBookmarked && (
            <div className="self-start sm:self-center sm:ml-auto sm:pl-4">
              <button
                className="bookmark-button flex items-center gap-1 text-[var(--q3-neutral-default)] px-2 py-1 text-sm cursor-pointer"
                style={{
                  borderRadius: '42px',
                  border: '1px solid var(--q3-stroke-normal)',
                  background: 'var(--q3-surface-default)',
                }}
                onClick={handleBookmarkClick}
                disabled={isAnyDrawerOpen}
              >
                <BookmarkSimpleIcon size={16} weight="fill" />
                <span>Bookmarked</span>
              </button>
            </div>
          )}
        </div>

        {showBookmarkDrawer && (
          <BookmarkDrawerGlobal
            questionId={questionid}
            packId={packId}
            testId={testId}
            handleShowDrawer={handleBookmarkDrawer}
            handleQuestionNotes={handleQuestionNotes}
            handleQuestionBoomark={handleQuestionBoomark}
          />
        )}

        {!simplifyMode && note && (
          <div className="text-[var(--q3-neutral-light)] text-sm pl-0 md:pl-8">
            <strong className="text-[var(--q3-neutral-default)]">Note:</strong> {note}
          </div>
        )}
      </div>
    </div>
  );
};
